import React, { useState, useEffect } from 'react'
import { MinusIcon } from '@heroicons/react/outline'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppToolTip } from '../AppToolTip/AppToolTip.component'
import { AppToolTipIcon } from '../AppToolTip/AppToolTipIcon'
import { AppUpTrendIcon } from '../AppSvgIcons/AppUpTrendIcon'
import { AppDownTrendIcon } from '../AppSvgIcons/AppDownTrendIcon'
import { AppRefreshIcon } from '../AppSvgIcons/AppRefreshIcon'

export type trendDirType = 'up' | 'down' | 'neutral'
interface StatsCardProps {
  title: string
  titleTooltip?: string
  subHeading: string
  subHeadingTooltip?: string
  value: string | number
  valueTooltip?: string
  changeValue: string
  changeTrend: trendDirType
  subValue?: {
    prefix: string
    value: string | number
    suffix: string
    trend: trendDirType
  }
  period: 'This Month' | 'YTD'
  onPeriodChange?: (period: 'This Month' | 'YTD') => void
  isLoading?: boolean
  errorMessage?: string
  errorMessageTooltip?: string
  hideMetric?: boolean
  dataSources?: string
  onRetry?: () => void
}

export const AppStatsCard: React.FC<StatsCardProps> = ({
  title,
  titleTooltip,
  subHeading,
  subHeadingTooltip,
  value,
  valueTooltip,
  changeValue,
  changeTrend,
  subValue,
  period,
  onPeriodChange,
  isLoading,
  errorMessage,
  errorMessageTooltip,
  hideMetric,
  dataSources,
  onRetry,
}) => {
  const getTrendColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return 'text-app-green-70'
      case 'down':
        return 'text-app-red-5'
      case 'neutral':
        return 'text-app-orange-15'
    }
  }

  const getTrendBgColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return 'bg-app-green-20'
      case 'down':
        return 'bg-app-red-20'
      case 'neutral':
        return 'bg-app-orange-20'
    }
  }

  const getTrendStrokeColor = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return '#629C28'
      case 'down':
        return '#D5394A'
      case 'neutral':
        return '#F07006'
    }
  }

  const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
    switch (trend) {
      case 'up':
        return <AppUpTrendIcon stroke={getTrendStrokeColor(trend)} />
      case 'down':
        return <AppDownTrendIcon stroke={getTrendStrokeColor(trend)} />
      case 'neutral':
        return <MinusIcon className="w-4 h-4" stroke={getTrendStrokeColor(trend)} />
    }
  }

  // Track if loading has been going on for a while (show "Hang tight" message)
  const [isLongLoading, setIsLongLoading] = useState(false)

  useEffect(() => {
    if (!isLoading) {
      setIsLongLoading(false)
      return
    }

    // Set a timer to show "Hang tight" message after 3 seconds
    const timer = setTimeout(() => {
      setIsLongLoading(true)
    }, 3000)

    return () => {
      clearTimeout(timer)
      setIsLongLoading(false)
    }
  }, [isLoading])

  return (
    <>
      <style>{`
        .ytd-button-wrapper .button {
          padding-right: 2.5rem !important;
        }
      `}</style>
      <div className="w-full">
        <div className="space-y-4 smd:space-y-0 smd:flex smd:items-center smd:justify-between mb-4">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-normal font-inter">{title}</h2>
            {titleTooltip && (
              <AppToolTip variant="primary" name={`stats-card-${title}-title`} text={titleTooltip}>
                <AppToolTipIcon />
              </AppToolTip>
            )}
          </div>
          <div className="flex items-center gap-2">
            <AppButton
              onClick={() => onPeriodChange?.('This Month')}
              variant={period === 'This Month' ? 'primary' : 'secondary'}
              size="sm"
              label="This Month"
            />
            <div className="relative inline-flex items-center ytd-button-wrapper">
              <AppButton
                onClick={() => onPeriodChange?.('YTD')}
                variant={period === 'YTD' ? 'primary' : 'secondary'}
                size="sm"
                label="YTD"
              />
              <div
                className="absolute right-2 top-1/2 -translate-y-1/2 ml-2"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <AppToolTip
                  variant="primary"
                  name={`stats-card-${title}-ytd`}
                  text="Year to date (YTD) refers to the period beginning on the first day of the current calendar year or fiscal year up to the current date."
                >
                  <AppToolTipIcon color={period === 'YTD' ? '#FFFFFF' : '#5E5E5E'} />
                </AppToolTip>
              </div>
            </div>
          </div>
        </div>

        {isLoading ? (
          isLongLoading ? (
            // Middle state: "Hang tight" message when loading takes longer
            <div className="h-[180px] bg-white flex flex-col items-center justify-center font-medium text-sm rounded-xl border border-app-grey-25 space-y-3 shadow-md px-6">
              <div className="w-10 h-10 rounded-full bg-[#f59e0b] flex items-center justify-center">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8 4V8M8 12H8.01"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="text-center space-y-1">
                <p className="text-app-grey-90 text-base font-inter font-semibold">
                  Hang tight, we're still working on it
                </p>
                <p className="text-app-grey-60 text-sm font-inter font-normal">
                  Your data is taking a bit longer than expected. Thanks for your patience!
                </p>
              </div>
            </div>
          ) : (
            // First state: Skeleton loader
            <div className="bg-white rounded-lg border border-app-grey-30 px-6 py-4 shadow-md min-h-[120px]">
              <div className="animate-pulse space-y-3">
                {/* Top row - subheading and data sources skeleton */}
                <div className="flex items-center justify-between">
                  <div className="h-4 bg-app-grey-20 rounded w-20"></div>
                  {dataSources && <div className="h-4 bg-app-grey-20 rounded w-24"></div>}
                </div>

                {/* Middle row - value and trend skeleton */}
                <div className="flex items-center justify-between">
                  <div className="h-7 bg-app-grey-20 rounded w-32"></div>
                  <div className="h-6 bg-app-grey-20 rounded-md w-16"></div>
                </div>

                {/* Bottom row - subvalue skeleton */}
                <div className="h-3 bg-app-grey-20 rounded w-40"></div>
              </div>
            </div>
          )
        ) : (
          <>
            {!hideMetric ? (
              <>
                {errorMessage ? (
                  <div className="h-[180px] bg-white flex flex-col items-center justify-center font-medium text-sm rounded-xl border border-app-grey-25 space-y-3 shadow-md px-6">
                    <div className="w-10 h-10 rounded-full bg-[#FF5257] flex items-center justify-center">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 4L4 12M12 12L4 4"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-app-grey-90 text-base font-inter font-semibold">
                        We hit a snag
                      </p>
                      <p className="text-app-grey-60 text-sm font-inter font-normal">
                        This happens sometimes. Do you mind giving it another try?
                      </p>
                    </div>
                    {onRetry && (
                      <AppButton
                        variant="secondary"
                        size="sm"
                        label="Try again"
                        onClick={onRetry}
                        RightIcon={AppRefreshIcon}
                      />
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg border border-app-grey-30 px-6 py-4 shadow-md min-h-[120px]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-app-grey-90 text-sm font-inter font-normal">
                          {subHeading}
                        </span>
                        {subHeadingTooltip && (
                          <AppToolTip
                            variant="primary"
                            name={`stats-card-${title}-client-count`}
                            text={subHeadingTooltip}
                          >
                            <AppToolTipIcon />
                          </AppToolTip>
                        )}
                      </div>
                      {dataSources && (
                        <div className="text-app-grey-90 text-sm font-inter font-normal">
                          {dataSources}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-2xl font-normal font-inter">{value}</h3>
                        {valueTooltip && (
                          <AppToolTip
                            variant="primary"
                            name={`stats-card-${title}-value`}
                            text={valueTooltip}
                          >
                            <AppToolTipIcon />
                          </AppToolTip>
                        )}
                      </div>
                      {changeValue && (
                        <div
                          className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md ${getTrendBgColor(
                            changeTrend
                          )}`}
                        >
                          {getTrendIcon(changeTrend)}
                          <span
                            className={`${getTrendColor(
                              changeTrend
                            )} ml-1 text-sm font-inter font-normal`}
                          >
                            {changeValue}
                          </span>
                        </div>
                      )}
                    </div>

                    {subValue && (
                      <div className="flex items-center gap-2">
                        <div className={`flex items-center gap-1`}>
                          <span
                            className={`${getTrendColor(
                              subValue.trend
                            )} text-sm font-inter font-normal`}
                          >
                            {subValue.prefix}
                          </span>
                          <span
                            className={`${getTrendColor(
                              subValue.trend
                            )} text-sm font-inter font-normal`}
                          >
                            {subValue.value}
                          </span>
                        </div>
                        <span
                          className={`${getTrendColor(
                            subValue.trend
                          )} text-sm font-inter font-normal`}
                        >
                          {subValue.suffix}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="h-[120px] bg-app-white-100 flex items-center justify-center font-medium text-sm rounded-xl border border-app-grey-25">
                You cannot view this information
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
